import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Star, Loader2, MessageSquare, Mail, QrCode, CheckCircle2, XCircle, Image as ImageIcon } from "lucide-react";

export const ReviewRequestDialog = ({ open, onOpenChange, customer }) => {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ sms_text: "", email_subject: "", email_body: "", send_sms: true, send_email: true, photo_ids: [] });

  useEffect(() => {
    if (!open || !customer) return;
    setLoading(true);
    axios.get(`${API}/customers/${customer.lead_id}/review-request/draft`)
      .then(res => {
        setDraft(res.data);
        setForm({
          sms_text: res.data.sms_text,
          email_subject: res.data.email_subject,
          email_body: res.data.email_body,
          send_sms: !!res.data.customer_phone,
          send_email: !!res.data.customer_email,
          photo_ids: res.data.selected_photo_ids || [],
        });
      })
      .catch(() => toast.error("Failed to load review request draft"))
      .finally(() => setLoading(false));
  }, [open, customer]);

  const handleSend = async () => {
    if (!form.send_sms && !form.send_email) return toast.error("Select at least one channel");
    setSending(true);
    try {
      const res = await axios.post(`${API}/customers/${customer.lead_id}/review-request`, form);
      const parts = [];
      if (form.send_sms) parts.push(res.data.sms_sent ? "SMS sent" : "SMS failed");
      if (form.send_email) parts.push(res.data.email_sent ? "Email sent" : "Email failed");
      const ok = (!form.send_sms || res.data.sms_sent) && (!form.send_email || res.data.email_sent);
      (ok ? toast.success : toast.warning)(`Review request: ${parts.join(", ")}`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send review request");
    } finally {
      setSending(false);
    }
  };

  const lastSent = draft?.history?.[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="review-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
            Project Closeout &amp; Google Review Request
          </DialogTitle>
        </DialogHeader>

        {loading || !draft ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
        ) : !draft.review_url ? (
          <p className="text-sm text-red-600 py-6" data-testid="review-no-link">
            No Google review link configured. Add it under Settings → Google Review URL first.
          </p>
        ) : (
          <div className="space-y-5">
            {lastSent && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="review-last-sent">
                Already sent to this customer on {new Date(lastSent.sent_at).toLocaleString()}
                {lastSent.sms_sent && " (SMS)"}{lastSent.email_sent && " (Email)"}. Sending again will send a duplicate.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="md:col-span-2 space-y-5">
                <ChannelBlock
                  icon={MessageSquare} title="Text Message" enabled={form.send_sms}
                  onToggle={v => setForm({ ...form, send_sms: v })}
                  recipient={draft.customer_phone} missing="No phone on file" testId="review-sms"
                >
                  <Textarea rows={6} value={form.sms_text} onChange={e => setForm({ ...form, sms_text: e.target.value })}
                    className="text-sm" data-testid="review-sms-text" />
                  <p className="text-xs text-slate-400 mt-1">{form.sms_text.length} characters · QR code attached as image (MMS)</p>
                </ChannelBlock>

                <ChannelBlock
                  icon={Mail} title="Email" enabled={form.send_email}
                  onToggle={v => setForm({ ...form, send_email: v })}
                  recipient={draft.customer_email} missing="No email on file" testId="review-email"
                >
                  <Label className="text-xs text-slate-500">Subject</Label>
                  <Input value={form.email_subject} onChange={e => setForm({ ...form, email_subject: e.target.value })}
                    className="mb-2" data-testid="review-email-subject" />
                  <Label className="text-xs text-slate-500">Message</Label>
                  <Textarea rows={14} value={form.email_body} onChange={e => setForm({ ...form, email_body: e.target.value })}
                    className="text-sm" data-testid="review-email-body" />
                  <p className="text-xs text-slate-400 mt-1">A "Leave us a 5-Star Google Review" button and the QR code are added automatically below your message.</p>
                  <PhotoPicker photos={draft.photos || []} selected={form.photo_ids} onChange={ids => setForm({ ...form, photo_ids: ids })} />
                </ChannelBlock>
              </div>

              <div className="space-y-3">
                <div className="border rounded-lg p-4 bg-slate-50 text-center">
                  <p className="text-xs font-medium text-slate-600 flex items-center justify-center gap-1 mb-2"><QrCode className="w-3.5 h-3.5" /> QR Code Attachment</p>
                  <img src={draft.qr_url} alt="Google review QR" className="w-36 h-36 mx-auto bg-white rounded border" data-testid="review-qr-preview" />
                  <a href={draft.review_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline break-all block mt-2" data-testid="review-link">
                    {draft.review_url}
                  </a>
                </div>
                {draft.history?.length > 0 && (
                  <div className="border rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-600 mb-2">Send History</p>
                    <div className="space-y-1.5 max-h-40 overflow-y-auto">
                      {draft.history.map(h => (
                        <div key={h.request_id} className="text-xs text-slate-500 flex items-center gap-1.5">
                          {h.sms_sent || h.email_sent ? <CheckCircle2 className="w-3 h-3 text-green-500" /> : <XCircle className="w-3 h-3 text-red-400" />}
                          {new Date(h.sent_at).toLocaleDateString()}
                          {h.sms_sent && <span className="bg-slate-100 px-1 rounded">SMS</span>}
                          {h.email_sent && <span className="bg-slate-100 px-1 rounded">Email</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="review-cancel-btn">Cancel</Button>
          <Button onClick={handleSend} disabled={sending || loading || !draft?.review_url} data-testid="review-send-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
            Send Review Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MAX_PHOTOS = 3;

const PhotoPicker = ({ photos, selected, onChange }) => {
  if (!photos.length) return null;
  const toggle = (id) => {
    if (selected.includes(id)) return onChange(selected.filter(x => x !== id));
    if (selected.length >= MAX_PHOTOS) return;
    onChange([...selected, id]);
  };
  return (
    <div className="mt-3 pt-3 border-t" data-testid="review-photo-picker">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs text-slate-500 flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> Finished-project photos in email ({selected.length}/{MAX_PHOTOS})</Label>
        {selected.length > 0 && <button type="button" className="text-xs text-slate-400 hover:text-red-600" onClick={() => onChange([])} data-testid="review-photos-clear">Clear</button>}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {photos.map(p => {
          const on = selected.includes(p.photo_id);
          return (
            <button key={p.photo_id} type="button" onClick={() => toggle(p.photo_id)} title={`${p.project_name || ""} · ${p.tag || "photo"}`}
              className={`relative shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-colors ${on ? "border-indigo-600" : "border-transparent hover:border-slate-300"}`}
              data-testid={`review-photo-${p.photo_id}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} alt="" className="w-full h-full object-cover" />
              {p.tag && <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] px-1 capitalize">{p.tag}</span>}
              {on && <span className="absolute top-1 right-1 bg-indigo-600 rounded-full"><CheckCircle2 className="w-4 h-4 text-white" /></span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const ChannelBlock = ({ icon: Icon, title, enabled, onToggle, recipient, missing, testId, children }) => (
  <div className={`border rounded-lg p-4 transition-opacity ${enabled ? "" : "opacity-60"}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-indigo-600" />
        <span className="font-medium text-sm">{title}</span>
        <span className="text-xs text-slate-500">{recipient ? `→ ${recipient}` : missing}</span>
      </div>
      <Switch checked={enabled} onCheckedChange={onToggle} disabled={!recipient} data-testid={`${testId}-toggle`} />
    </div>
    {enabled && children}
  </div>
);
